import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  message,
  Upload,
  Button,
  DatePicker,
  Checkbox,
  Row,
  Col,
} from "antd";
import { UploadOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import axiosInstance from "../../utils/axios";
import dayjs from "dayjs";

const EventModal = ({ open, setOpen, selectedId, refreshFetch }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [sections, setSections] = useState([{
    title: '',
    price: '',
    count: '',
    released: '',
    reserved: '',
    group_prices: [{ min_quantity: '', group_price: '' }]
  }]);

  const isUpdate = Boolean(selectedId);

  useEffect(() => {
    if (open) {
      if (isUpdate) {
        fetchDetail();
      } else {
        form.resetFields();
        setSelectedItem(null);
        setThumbnail(null);
        setSections([{ title: '', price: '', count: '', released: '', reserved: '', group_prices: [{ min_quantity: '', group_price: '' }] }]);
      }
    }
  }, [open, selectedId]);

  const fetchDetail = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(
        `news_and_events/events/${selectedId}/`,
      );
      setSelectedItem(data);
      form.setFieldsValue({
        heading: data.heading,
        subtitle: data.subtitle,
        description: data.description,
        book_start: data.book_start,
        start_date: dayjs(data.start_date),
        end_date: dayjs(data.end_date),
        time: data.time,
        link: data.link,
        venue: data.venue,
        publish: data.publish,
      });

      if (data.thumbnail) {
        setThumbnail({
          uid: "-1",
          name: "thumbnail.jpg",
          status: "done",
          url: data.thumbnail,
        });
      }

      const safeSections = Array.isArray(data.sections)
        ? data.sections.map(s => ({
          id: s.id, // VERY IMPORTANT
          title: s.title,
          count: s.count,
          price: s.price,
          released: s.released,
          reserved: s.reserved,
          group_prices: Array.isArray(s.group_prices)
            ? s.group_prices.map(g => ({
              id: g.id, // ALSO IMPORTANT
              min_quantity: g.min_quantity,
              group_price: g.group_price
            }))
            : []
        }))
        : [];
      setSections(safeSections.length > 0 ? safeSections : [{
        title: '', price: '', count: '', released: '', reserved: '',
        group_prices: [{ min_quantity: '', group_price: '' }]
      }]);
    } catch (err) {
      message.error("Cannot fetch event details");
    } finally {
      setLoading(false);
    }
  };

  const validateImage = (file) => {
    const isImage = ["image/jpeg", "image/png", "image/jpg"].includes(
      file.type,
    );
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isImage) {
      message.error("Only JPG/PNG files are allowed!");
      return Upload.LIST_IGNORE;
    }
    if (!isLt2M) {
      message.error("Image must be smaller than 2MB!");
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const getOriginFile = (file) => {
    if (!file) return null;
    return file.originFileObj || null;
  };

  const handleCancel = () => {
    setOpen(false);
    form.resetFields();
    setSelectedItem(null);
    setThumbnail(null);
    setSections([{ title: '', price: '', count: '', released: '', reserved: '' }]);
  };

  const addSection = () => {
    setSections([...sections, { title: '', price: '', count: '', released: '', reserved: '', group_prices: [{ min_quantity: '', group_price: '' }] }]);
  };

  const removeSection = (index) => {
    setSections(sections.filter((_, i) => i !== index));
  };

  const updateSection = (index, field, value) => {
    const updated = sections.map((section, i) =>
      i === index ? { ...section, [field]: value } : section
    );
    setSections(updated);
  };

  const updateGroupPrice = (sectionIndex, rowIndex, field, value) => {
    const updatedSections = sections.map((section, sIndex) =>
      sIndex === sectionIndex
        ? {
          ...section,
          group_prices: section.group_prices.map((row, rIndex) =>
            rIndex === rowIndex ? { ...row, [field]: value } : row
          )
        }
        : section
    );
    setSections(updatedSections);
  };

  const addGroupPriceRow = (sectionIndex) => {
    const updatedSections = sections.map((section, sIndex) =>
      sIndex === sectionIndex
        ? { ...section, group_prices: [...section.group_prices, { min_quantity: '', group_price: '' }] }
        : section
    );
    setSections(updatedSections);
  };

  const removeGroupPriceRow = (sectionIndex, rowIndex) => {
    const updatedSections = sections.map((section, sIndex) =>
      sIndex === sectionIndex
        ? {
          ...section,
          group_prices: section.group_prices.filter((_, rIndex) => rIndex !== rowIndex)
        }
        : section
    );
    setSections(updatedSections);
  };

  const handleFinish = async (values) => {
    if (!thumbnail && !isUpdate) {
      message.error("Thumbnail is required");
      return;
    }

    setLoading(true);
    const fd = new FormData();
    fd.append("heading", values.heading);
    fd.append("subtitle", values.subtitle);
    fd.append("description", values.description || "");
    fd.append("book_start", values.book_start || false);
    fd.append("start_date", values.start_date.format("YYYY-MM-DD"));
    fd.append("end_date", values.end_date.format("YYYY-MM-DD"));
    fd.append("venue", values.venue);
    fd.append("time", values.time);
    fd.append("link", values.link);
    fd.append("publish", values.publish || false);

    // Add sections data
    const processedSections = (sections || []).map(section => ({
      id: section.id, // ✅ IMPORTANT
      title: section.title,
      price: parseFloat(section.price) || 0,
      count: parseInt(section.count) || 0,
      released: parseInt(section.released) || 0,
      reserved: parseInt(section.reserved) || 0,
      group_prices: (section.group_prices || [])
        .filter(row => row.min_quantity > 0 && row.group_price >= 0)
        .map(row => ({
          id: row.id, // ✅ IMPORTANT
          min_quantity: parseInt(row.min_quantity),
          group_price: parseFloat(row.group_price)
        }))
    }));
    fd.append("sections", JSON.stringify(processedSections));

    const thumbFile = getOriginFile(thumbnail);
    if (thumbFile) fd.append("thumbnail", thumbFile);

    try {
      if (isUpdate) {
        await axiosInstance.patch(
          `news_and_events/events/${selectedItem.id}/`,
          fd,
        );
        message.success("Event updated successfully!");
      } else {
        await axiosInstance.post(`news_and_events/events/`, fd);
        message.success("Event added successfully!");
      }
      form.resetFields();
      refreshFetch();
      setOpen(false);
    } catch (error) {
      console.error(error);
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={selectedItem ? "Edit Event" : "Add new Event"}
      open={open}
      onCancel={handleCancel}
      destroyOnHidden
      width={1000}
      okButtonProps={{
        autoFocus: true,
        htmlType: "submit",
        loading,
        disabled: loading,
      }}
      onOk={() => form.submit()}
    >
      <Form
        layout="vertical"
        form={form}
        name="event_form"
        onFinish={handleFinish}
      >
        <Form.Item
          label="Heading"
          name="heading"
          rules={[{ required: true, message: "Please enter heading" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Subtitle"
          name="subtitle"
          rules={[{ required: true, message: "Please enter subtitle" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Description"
          name="description"
        >
          <Input.TextArea rows={3} placeholder="Enter a specific message for users registering this event" />
        </Form.Item>

        <Form.Item
          label="Venue"
          name="venue"
          rules={[{ required: true, message: "Please enter place" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Start Date"
          name="start_date"
          rules={[{ required: true, message: "Please select start date" }]}
        >
          <DatePicker style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item
          label="End Date"
          name="end_date"
          rules={[{ required: true, message: "Please select end date" }]}
        >
          <DatePicker style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item
          label="Time (Eg: 08:00 - 12:00 pm)"
          name="time"
          rules={[{ required: true, message: "Please enter place" }]}
        >
          <Input placeholder="08:00 - 12:00 pm" />
        </Form.Item>
        <Form.Item
          label="Link"
          name="link"
        >
          <Input placeholder="https://getam.com" />
        </Form.Item>
        <Form.Item
          label="Thumbnail (Required)"
          required
          validateStatus={!thumbnail && !isUpdate ? "error" : "success"}
          help={!thumbnail && !isUpdate && "Thumbnail is required"}
        >
          <Upload
            maxCount={1}
            fileList={thumbnail ? [thumbnail] : []}
            beforeUpload={(file) => {
              const valid = validateImage(file);
              if (valid !== true) return valid;

              setThumbnail({
                uid: Date.now().toString(),
                name: file.name,
                status: "done",
                originFileObj: file,
              });

              return false;
            }}
            onRemove={() => setThumbnail(null)}
          >
            <Button icon={<UploadOutlined />}>Upload Thumbnail</Button>
          </Upload>
        </Form.Item>

        <Form.Item name="book_start" valuePropName="checked">
          <Checkbox>Enable Booking</Checkbox>
        </Form.Item>

        <Form.Item name="publish" valuePropName="checked">
          <Checkbox>Publish</Checkbox>
        </Form.Item>

        <div style={{ marginTop: 20 }}>
          <h4>Event Sections</h4>
          {sections?.map((section, index) => (
            <div key={index} style={{ border: '1px solid #d9d9d9', padding: 16, marginBottom: 16, borderRadius: 4 }}>
              <Row gutter={16}>
                <Col span={5}>
                  <Form.Item label="Title">
                    <Input
                      value={section.title}
                      onChange={(e) => updateSection(index, 'title', e.target.value)}
                      placeholder="e.g., VIP"
                    />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item label="Base Price">
                    <Input
                      type="number"
                      value={section.price}
                      onChange={(e) => updateSection(index, 'price', e.target.value)}
                      placeholder="28.99"
                    />
                  </Form.Item>
                </Col>
                <Col span={3}>
                  <Form.Item label="Count">
                    <Input
                      type="number"
                      value={section.count}
                      onChange={(e) => updateSection(index, 'count', e.target.value)}
                      placeholder="0"
                    />
                  </Form.Item>
                </Col>
                <Col span={3}>
                  <Form.Item label="Released">
                    <Input
                      type="number"
                      value={section.released}
                      onChange={(e) => updateSection(index, 'released', e.target.value)}
                      placeholder="0"
                    />
                  </Form.Item>
                </Col>
                <Col span={3}>
                  <Form.Item label="Reserved">
                    <Input
                      type="number"
                      value={section.reserved}
                      onChange={(e) => updateSection(index, 'reserved', e.target.value)}
                      placeholder="0"
                    />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item label="Action">
                    <Button
                      type="danger"
                      icon={<DeleteOutlined />}
                      onClick={() => removeSection(index)}
                      disabled={sections.length === 1}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Group Prices Inline Table */}
              <div style={{ marginTop: 12 }}>
                <h5 style={{ marginBottom: 8, color: '#1890ff' }}>Group Prices</h5>
                {section.group_prices?.map((row, rowIndex) => (
                  <Row key={rowIndex} gutter={12} style={{ marginBottom: 8 }} align="middle">
                    <Col span={6}>
                      <InputNumber
                        value={row.min_quantity}
                        onChange={(val) => updateGroupPrice(index, rowIndex, 'min_quantity', val)}
                        placeholder="Min Qty"
                        min={2}
                        size="small"
                      />
                    </Col>
                    <Col span={6}>
                      <InputNumber
                        value={row.group_price}
                        onChange={(val) => updateGroupPrice(index, rowIndex, 'group_price', val)}
                        placeholder="Price"
                        min={0}
                        step={0.01}
                        size="small"
                      />
                    </Col>
                    <Col span={12}>
                      <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        size="small"
                        onClick={() => removeGroupPriceRow(index, rowIndex)}
                        disabled={section.group_prices.length === 1}
                      />
                    </Col>
                  </Row>
                ))}
                <Button
                  type="link"
                  onClick={() => addGroupPriceRow(index)}
                  size="small"
                  icon={<PlusOutlined />}
                >
                  Add Group Price
                </Button>
              </div>
            </div>
          ))}
          <Button type="dashed" icon={<PlusOutlined />} onClick={addSection} style={{ width: '100%' }}>
            Add Section
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default EventModal;
